import { formatDate } from '../../utils/dateUtils';
import { Button, Badge, Dropdown, Icon, SidePanel } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  due_date?: string;
  client_id?: string;
  client_name?: string;
  priority: number;
  created_at: string;
  updated_at: string;
  website_id?: string;
  website_url?: string;
  website_title?: string;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  metadata?: Record<string, unknown>;
}

interface EditForm {
  title: string;
  status: string;
  due_date: string;
  description: string;
}

interface WebsiteTaskDetailModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedTask: Task | null;
  setSelectedTask: (task: Task | null) => void;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  editForm: EditForm;
  setEditForm: (editForm: EditForm) => void;
  handleSaveTaskChanges: () => Promise<void>;
  handleStatusChange: (taskId: string, newStatus: string) => Promise<void>;
  setError: (error: string | null) => void;
}

const STATUS_TONES: Record<string, BadgeTone> = {
  Scheduled: 'info',
  'In Progress': 'warn',
  Submitted: 'accent',
  Complete: 'up',
  Blocked: 'down',
};

const QUICK_STATUSES = ['Scheduled', 'In Progress', 'Submitted', 'Complete', 'Blocked'];

export default function WebsiteTaskDetailModal(props: WebsiteTaskDetailModalProps) {
  const {
    isOpen,
    setIsOpen,
    selectedTask,
    editMode,
    setEditMode,
    editForm,
    setEditForm,
    handleSaveTaskChanges,
    handleStatusChange,
  } = props;

  if (!selectedTask) return null;

  return (
    <SidePanel
      open={isOpen}
      onClose={() => setIsOpen(false)}
      width={480}
      eyebrow="Website Task"
      title={editMode ? 'Edit Task' : selectedTask.title}
      footer={
        editMode ? (
          <>
            <Button icon="check" onClick={handleSaveTaskChanges}>
              Save Changes
            </Button>
            <Button variant="ghost" onClick={() => setEditMode(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" icon="pencil" onClick={() => setEditMode(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </>
        )
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {editMode ? (
          <>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Title</div>
              <input
                className="ck-title-input"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Task title..."
              />
            </div>

            <Dropdown
              label="Status"
              options={QUICK_STATUSES.map((s) => ({ value: s, label: s }))}
              value={editForm.status}
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              placeholder="Select status..."
            />

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Due Date</div>
              <input
                type="date"
                className="ck-date-input"
                value={editForm.due_date}
                onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
              />
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Description</div>
              <textarea
                className="ck-textarea"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={6}
                placeholder="Task description..."
              />
            </div>
          </>
        ) : (
          <>
            {/* Current status + quick set */}
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Status</div>
              <div className="flex items-center flex-wrap" style={{ gap: '8px' }}>
                <Badge tone={STATUS_TONES[selectedTask.status] || 'idle'} dot>
                  {selectedTask.status}
                </Badge>
                {QUICK_STATUSES.filter((s) => s !== selectedTask.status).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(selectedTask.id, status)}
                    className="pressable hover-wash"
                    style={{
                      height: '22px',
                      padding: '0 9px',
                      borderRadius: 'var(--radius-pill)',
                      border: '1px solid var(--border-hairline)',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-2xs)',
                      letterSpacing: 'var(--tracking-label)',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selectedTask.due_date && (
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Icon name="clock" size={14} style={{ color: 'var(--text-faint)' }} />
                  <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    Due {formatDate(selectedTask.due_date, 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              {selectedTask.client_name && (
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Icon name="building-2" size={14} style={{ color: 'var(--text-faint)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {selectedTask.client_name}
                  </span>
                </div>
              )}

              {selectedTask.website_title && (
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Icon name="globe" size={14} style={{ color: 'var(--text-faint)' }} />
                  <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {selectedTask.website_title}
                  </span>
                </div>
              )}

              {selectedTask.assigned_to_name && (
                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Icon name="user" size={14} style={{ color: 'var(--text-faint)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {selectedTask.assigned_to_name}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            <div
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
              }}
            >
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Description</div>
              {selectedTask.description ? (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>
                  {selectedTask.description}
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                  No description provided for this task
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </SidePanel>
  );
}
